import styled, { css } from 'styled-components'

import calendarIcon from '@/assets/svgs/calendar_icon.svg'
import crossIcon from '@/assets/svgs/cross_icon.svg'
import { hexToRgb } from '@/lib/helpers'

import type { WrapperProps } from './types'

export const Wrapper = styled.div<WrapperProps>`
  .react-datepicker {
    left: 15px;
    font-family: ${({ theme }) => theme.fonts.mainFont};
    font-size: 14px;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    ${({ $isSingleMonth }) =>
      $isSingleMonth
        ? css`
            display: flex;
            justify-content: flex-end;
            width: 260px;
          `
        : css`
            width: 665px;
          `}
  }
  .react-datepicker__input-container::after {
    ${({ $isIconHidden }) =>
      $isIconHidden &&
      css`
        display: none;
      `}
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    top: ${({ $isWrapperSmall }) => ($isWrapperSmall ? '11px' : '14px')};
    left: 15px;
    background-image: url(${calendarIcon});
    background-position: center;
    background-size: 18px;
    background-repeat: no-repeat;
    pointer-events: none;
  }

  .react-datepicker__input-container input {
    height: 50px;
    padding: 14px;
    padding-left: 40px;
    color: ${({ theme }) => theme.colors.sapphireDark};
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.crystalBay};
    border-radius: 8px;
    width: 100%;
    transition:
      border-color,
      ease 350ms;
    font-family: ${({ theme }) => theme.fonts.mainFont};
    font-weight: normal;
    font-size: 16px;
    line-height: 1.2;
    padding-right: 44px;
    min-height: ${({ $isWrapperSmall }) => ($isWrapperSmall ? '40px' : '48px')};

    :read-only {
      color: ${({ theme }) => theme.colors.slateGray};
      background: ${({ theme }) => theme.colors.catskillWhite};
    }

    ::placeholder {
      color: ${({ theme }) => theme.colors.blueAura};
      opacity: 1;
    }

    ${({ $error }) =>
      $error &&
      css`
        border: 1px solid ${({ theme }) => theme.colors.error};
      `}
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker {
    border: none;
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0px 0px 26px rgba(98, 106, 114, 0.2);
    border-radius: 8px;
  }

  .react-datepicker__header {
    background: ${({ theme }) => theme.colors.white};
    border: none;
    font-family: ${({ theme }) => theme.fonts.mainFont};
    font-size: 16px;
    font-weight: normal;
    line-height: 1.2;
  }

  .react-datepicker__header--custom {
    border-radius: 0;
    padding: 0;
    border-top-left-radius: 8px;
  }

  .react-datepicker__header--custom + .react-datepicker__header--custom {
    border-radius: 0;
    border-top-right-radius: 8px;
  }

  .react-datepicker__header-title {
    padding: 14px 0;
  }
  .react-datepicker__month--in-range {
    color: ${({ theme }) => theme.colors.altoVitaMain};
    background-color: ${({ theme }) => theme.colors.errigalWhite};
  }

  .react-datepicker__month-container,
  .react-datepicker__year--container {
    box-shadow: none;
    border-radius: 0;
    width: 292px;
    height: 335px;
  }

  .react-datepicker__month-container .react-datepicker__header--custom {
    border-radius: 0;
    border-top-left-radius: 8px;
  }

  .react-datepicker__current-month {
    font-family: ${({ theme }) => theme.fonts.mainFont};
    font-weight: normal;
    font-size: 15px;
    color: ${({ theme }) => theme.colors.slateGray};
    padding: 14px 0;
  }

  .react-datepicker__day-names {
    padding-top: 19px;
    border-top: 1px solid ${({ theme }) => theme.colors.showFlake};

    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    text-transform: uppercase;
  }

  .react-datepicker__day-name {
    font-family: ${({ theme }) => theme.fonts.mainFont};
    color: ${({ theme }) => theme.colors.slateGray};
    font-weight: 400;
  }

  .react-datepicker__month {
    margin: 18px 20px;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 36px;
    border-radius: 4px;
  }

  .react-datepicker__month-wrapper {
    display: flex;
    justify-content: space-between;
    margin-top: 41px;
  }

  .react-datepicker__day {
    font-family: ${({ theme }) => theme.fonts.mainFont};
    color: ${({ theme }) => theme.colors.sapphireDark};
    line-height: 36px;
    margin: 0;
    border-radius: 0;
    font-weight: 400;
  }

  .react-datepicker__month-text {
    font-family: ${({ theme }) => theme.fonts.mainFont};
    color: ${({ theme }) => theme.colors.sapphireDark};
    line-height: 36px;
    margin: 0;
    border-radius: 0;
    font-weight: 400;
    width: 60px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .react-datepicker__year-text {
    font-family: ${({ theme }) => theme.fonts.mainFont};
    color: ${({ theme }) => theme.colors.sapphireDark};
    line-height: 36px;
    margin: 0;
    border-radius: 0;
    width: 60px;
    margin-left: 15px;
    margin-right: 15px;
    margin-top: 40px;
    font-weight: 400;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .react-datepicker__day--selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    border-radius: 4px;
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.sapphireDark};
    font-weight: 500;
  }

  .react-datepicker__year-wrapper {
    max-width: 300px;
  }

  .react-datepicker__day--outside-month {
    color: ${({ theme }) => theme.colors.slateGray};
  }

  .react-datepicker__navigation {
    top: 8px;
    border: none;
  }

  .react-datepicker__navigation-icon {
    top: 4px;
  }

  .react-datepicker__close-icon {
    right: 38px;
    top: 1px;

    ::after {
      content: '';
      background: ${({ theme }) => theme.colors.white};
      background-image: url(${crossIcon});
      background-position: center;
      background-size: 18px;
      width: 18px;
      height: 18px;
      left: 15px;
    }
  }

  .react-datepicker__day--today {
    color: ${({ theme }) => theme.colors.sapphireDark};
    background: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.sapphireDark};
    border-radius: 4px;
  }

  .react-datepicker__day--in-range {
    background: ${({ theme }) => {
      const { r, g, b } = hexToRgb(theme.colors.slateGray)
      return `rgb(${r},${g},${b}, 0.1)`
    }};
  }

  .react-datepicker__day--in-selecting-range {
    color: ${({ theme }) => theme.colors.sapphireDark};
    background: ${({ theme }) => {
      const { r, g, b } = hexToRgb(theme.colors.slateGray)
      return `rgb(${r},${g},${b}, 0.1)`
    }};
  }

  .react-datepicker__day--today .react-datepicker__day--in-range {
    color: ${({ theme }) => theme.colors.sapphireDark};
  }

  .react-datepicker__day--selected.react-datepicker__day--today,
  .react-datepicker__month-text--keyboard-selected {
    :hover {
      background: ${({ theme }) => theme.colors.sapphireDark};
      color: ${({ theme }) => theme.colors.white};
    }
  }

  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.sapphireDark};
    color: ${({ theme }) => theme.colors.white};
  }

  .react-datepicker__day--selecting-range-start,
  .react-datepicker__day--selecting-range-end {
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.sapphireDark} !important;
    color: ${({ theme }) => theme.colors.white};
  }

  .react-datepicker__navigation--month.react-datepicker__navigation--previous {
    left: 54px;
  }

  .react-datepicker__navigation--month.react-datepicker__navigation--next {
    right: 54px;
  }

  .react-datepicker__navigation--year .react-datepicker__navigation-icon,
  .react-datepicker__navigation--month .react-datepicker__navigation-icon {
    ::before {
      display: none;
    }
  }

  .react-datepicker__navigation--year,
  .react-datepicker__navigation--month {
    opacity: 0.5;

    :hover {
      opacity: 1;
    }
  }

  .react-datepicker__navigation--year .react-datepicker__navigation-icon--next,
  .react-datepicker__navigation--month
    .react-datepicker__navigation-icon--next {
    svg {
      transform: rotateY(180deg);
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }

  .react-datepicker__navigation--year
    .react-datepicker__navigation-icon--previous,
  .react-datepicker__navigation--month
    .react-datepicker__navigation-icon--previous {
    svg {
      position: absolute;
      bottom: 0;
      right: 0;
    }
  }

  .react-datepicker__navigation--year.react-datepicker__navigation--previous {
    left: 20px;
  }

  .react-datepicker__navigation--year.react-datepicker__navigation--next {
    right: 20px;
  }
`
